import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Grid,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { categoryApi } from '../../services/api';
import { API_PATHS } from '../../services/apiEndpoints';
import ItemDialog from './ItemDialog';

// Configuration for manageable categories
const MANAGED_CATEGORIES = [
  {
    label: 'Brands',
    apiPath: API_PATHS.BRANDS,
    idField: 'brand_id',
    nameField: 'brand_name',
    columns: [
      { id: 'brand_id', label: 'ID', minWidth: 50 },
      { id: 'brand_name', label: 'Name', minWidth: 170 },
      { id: 'brand_key', label: 'Key', minWidth: 100 },
    ],
    formFields: [
      { name: 'brand_name', label: 'Brand Name', type: 'text', required: true },
      { name: 'brand_key', label: 'Brand Key', type: 'text' },
    ],
  },
  {
    label: 'Sub-brands',
    apiPath: API_PATHS.SUB_BRANDS,
    idField: 'sub_brand_id',
    nameField: 'sub_brand_name',
    columns: [
      { id: 'sub_brand_id', label: 'ID', minWidth: 50 },
      { id: 'sub_brand_name', label: 'Name', minWidth: 170 },
      { id: 'sub_brand_key', label: 'Key', minWidth: 100 },
      { 
        id: 'brand', 
        label: 'Brand', 
        minWidth: 170, 
        render: (item, brands) => {
          const brand = brands.find(b => b.brand_id === item.brand);
          return brand ? brand.brand_name : 'N/A';
        },
        relatedDataSourceName: 'brands'
      },
    ],
    formFields: [
      { name: 'sub_brand_name', label: 'Sub-brand Name', type: 'text', required: true },
      { name: 'sub_brand_key', label: 'Sub-brand Key', type: 'text' },
      { 
        name: 'brand', 
        label: 'Brand', 
        type: 'select', 
        required: true, 
        optionsSource: API_PATHS.BRANDS,
        optionValueField: 'brand_id',
        optionLabelField: 'brand_name',
      },
    ],
    relatedDataSources: [
      { name: 'brands', path: API_PATHS.BRANDS }
    ]
  },
  {
    label: 'Campaign Types',
    apiPath: API_PATHS.CAMPAIGN_TYPES,
    idField: 'campaign_type_id',
    nameField: 'campaign_type_name',
    columns: [
      { id: 'campaign_type_id', label: 'ID', minWidth: 50 },
      { id: 'campaign_type_name', label: 'Name', minWidth: 170 },
    ],
    formFields: [
      { name: 'campaign_type_name', label: 'Campaign Type Name', type: 'text', required: true },
    ],
  },
  {
    label: 'Statuses',
    apiPath: API_PATHS.STATUSES,
    idField: 'status_id',
    nameField: 'status_name',
    columns: [
      { id: 'status_id', label: 'ID', minWidth: 50 },
      { id: 'status_name', label: 'Name', minWidth: 170 },
      { id: 'status_code', label: 'Code', minWidth: 100 },
      { id: 'status_description', label: 'Description', minWidth: 200 },
    ],
    formFields: [
      { name: 'status_name', label: 'Status Name', type: 'text', required: true },
      { name: 'status_code', label: 'Status Code', type: 'text' },
      { name: 'status_description', label: 'Description', type: 'text' },
    ],
  },
  {
    label: 'Media Types',
    apiPath: API_PATHS.MEDIA_TYPES,
    idField: 'media_type_channel_id',
    nameField: 'media_type_channel_name',
    columns: [
      { id: 'media_type_channel_id', label: 'ID', minWidth: 50 },
      { id: 'media_type_channel_name', label: 'Name', minWidth: 170 },
    ],
    formFields: [
      { name: 'media_type_channel_name', label: 'Media Type Name', type: 'text', required: true },
    ],
  },
  {
    label: 'Objectives',
    apiPath: API_PATHS.OBJECTIVES,
    idField: 'objective_kpi_id',
    nameField: 'objective_kpi_name',
    columns: [
      { id: 'objective_kpi_id', label: 'ID', minWidth: 50 },
      { id: 'objective_kpi_name', label: 'Name', minWidth: 170 },
    ],
    formFields: [
      { name: 'objective_kpi_name', label: 'Objective Name', type: 'text', required: true },
    ],
  },
  {
    label: 'Sub Media Types',
    apiPath: API_PATHS.SUB_MEDIA_TYPES,
    idField: 'sub_media_type_id',
    nameField: 'sub_media_type_name',
    columns: [
      { id: 'sub_media_type_id', label: 'ID', minWidth: 50 },
      { id: 'sub_media_type_name', label: 'Name', minWidth: 170 },
      { id: 'sub_media_type_key', label: 'Key', minWidth: 150 },
    ],
    formFields: [
      { name: 'sub_media_type_name', label: 'Sub Media Type Name', type: 'text', required: true },
      { name: 'sub_media_type_key', label: 'Sub Media Type Key', type: 'text' },
    ],
  },
  {
    label: 'Agencies',
    apiPath: API_PATHS.AGENCIES,
    idField: 'agency_id',
    nameField: 'agency_name',
    columns: [
      { id: 'agency_id', label: 'ID', minWidth: 50 },
      { id: 'agency_name', label: 'Name', minWidth: 170 },
      { id: 'agency_key', label: 'Key', minWidth: 100 },
    ],
    formFields: [
      { name: 'agency_name', label: 'Agency Name', type: 'text', required: true },
      { name: 'agency_key', label: 'Agency Key', type: 'text' },
    ],
  },
  {
    label: 'Digital Vendors',
    apiPath: API_PATHS.DIGITAL_VENDORS,
    idField: 'digital_vendor_id',
    nameField: 'digital_vendor_name',
    columns: [
      { id: 'digital_vendor_id', label: 'ID', minWidth: 50 },
      { id: 'digital_vendor_name', label: 'Name', minWidth: 170 },
    ],
    formFields: [
      { name: 'digital_vendor_name', label: 'Digital Vendor Name', type: 'text', required: true },
    ],
  },
  {
    label: 'Digital Ad Formats',
    apiPath: API_PATHS.DIGITAL_AD_FORMATS,
    idField: 'digital_ad_format_id',
    nameField: 'digital_ad_format_name',
    columns: [
      { id: 'digital_ad_format_id', label: 'ID', minWidth: 50 },
      { id: 'digital_ad_format_name', label: 'Name', minWidth: 170 },
    ],
    formFields: [
      { name: 'digital_ad_format_name', label: 'Digital Ad Format Name', type: 'text', required: true },
    ],
  },
  {
    label: 'OOH Formats',
    apiPath: API_PATHS.OOH_FORMATS,
    idField: 'ooh_format_id',
    nameField: 'ooh_format_name',
    columns: [
      { id: 'ooh_format_id', label: 'ID', minWidth: 50 },
      { id: 'ooh_format_name', label: 'Name', minWidth: 170 },
    ],
    formFields: [
      { name: 'ooh_format_name', label: 'OOH Format Name', type: 'text', required: true },
    ],
  },
  {
    label: 'Asset Types',
    apiPath: API_PATHS.ASSET_TYPES,
    idField: 'asset_video_length_image_type_id',
    nameField: 'asset_video_length_image_type_name',
    columns: [
      { id: 'asset_video_length_image_type_id', label: 'ID', minWidth: 50 },
      { id: 'asset_video_length_image_type_name', label: 'Name', minWidth: 170 },
    ],
    formFields: [
      { name: 'asset_video_length_image_type_name', label: 'Asset Type Name', type: 'text', required: true },
    ],
  },
];

function CategoryManagementPanel({ showSnackbar = () => {} }) {
  const [selectedCategoryPath, setSelectedCategoryPath] = useState('');
  const [categoryItems, setCategoryItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [relatedData, setRelatedData] = useState({});

  const [isItemDialogOpen, setIsItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null); 
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'

  const currentCategoryConfig = useMemo(() => {
    return MANAGED_CATEGORIES.find(cat => cat.apiPath === selectedCategoryPath);
  }, [selectedCategoryPath]);

  const fetchRelatedData = useCallback(async (sources) => {
    if (!sources || sources.length === 0) {
      setRelatedData({});
      return;
    }
    try {
      const dataPromises = sources.map(async (source) => {
        const response = await categoryApi.getCategoryItems(source.path);
        return { name: source.name, data: response.data.results || response.data };
      });
      const results = await Promise.all(dataPromises);
      const newRelatedData = results.reduce((acc, curr) => {
        acc[curr.name] = curr.data;
        return acc;
      }, {});
      setRelatedData(newRelatedData);
    } catch (err) {
      console.error('Error fetching related data:', err);
      showSnackbar('Failed to load some auxiliary data for display.', 'warning');
      setRelatedData({});
    } 
  }, [showSnackbar]);

  const fetchCategoryItems = useCallback(async () => {
    if (!selectedCategoryPath || !currentCategoryConfig) {
      setCategoryItems([]);
      setRelatedData({});
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await categoryApi.getCategoryItems(selectedCategoryPath);
      setCategoryItems(response.data.results || response.data); 

      if (currentCategoryConfig.relatedDataSources) {
        await fetchRelatedData(currentCategoryConfig.relatedDataSources);
      } else {
        setRelatedData({});
      }

    } catch (err) {
      console.error(`Error fetching ${currentCategoryConfig?.label}:`, err);
      setError(`Failed to load ${currentCategoryConfig?.label}.`);
      showSnackbar(`Failed to load ${currentCategoryConfig?.label}. Error: ${err.message}`, 'error');
      setCategoryItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCategoryPath, currentCategoryConfig, showSnackbar, fetchRelatedData]);

  useEffect(() => {
    if (selectedCategoryPath && currentCategoryConfig) {
        fetchCategoryItems();
    } else {
        setCategoryItems([]);
        setRelatedData({});
    }
  }, [selectedCategoryPath, currentCategoryConfig, fetchCategoryItems]);

  const handleCategoryChange = (event) => {
    setSelectedCategoryPath(event.target.value);
  };

  const handleOpenAddItemDialog = () => {
    setEditingItem(null); 
    setDialogMode('add');
    setIsItemDialogOpen(true);
  };

  const handleCloseItemDialog = () => {
    setIsItemDialogOpen(false);
    setEditingItem(null);
  };

  const handleItemSubmit = async (formData) => {
    if (!currentCategoryConfig) return;
    setIsLoading(true);
    try {
      let path = currentCategoryConfig.apiPath;
      if (!path.endsWith('/')) {
        path += '/';
      }

      if (editingItem) {
        // Update existing item
        await categoryApi.updateCategoryItem(`${path}${editingItem[currentCategoryConfig.idField]}/`, formData);
        showSnackbar(`${currentCategoryConfig.label} item updated successfully!`, 'success');
      } else {
        // Create new item
        await categoryApi.createCategoryItem(currentCategoryConfig.apiPath, formData); // For POST, often the path itself is enough if it's a list endpoint
        showSnackbar(`${currentCategoryConfig.label} item added successfully!`, 'success');
      }
      fetchCategoryItems(); 
      handleCloseItemDialog();
    } catch (err) {
      console.error(`Error submitting ${currentCategoryConfig.label}:`, err);
      const errorDetail = err.response?.data ? JSON.stringify(err.response.data) : err.message;
      showSnackbar(`Failed to submit ${currentCategoryConfig.label}. Error: ${errorDetail}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  const renderCellContent = (item, column) => {
    const value = item[column.id];
    if (column.render) {
        return column.render(item, relatedData[column.relatedDataSourceName] || []);
    }
    return value !== null && value !== undefined ? String(value) : 'N/A';
  };

  const handleEditItem = (item) => {
    // Ensure relatedData is passed or available if needed for pre-filling complex fields in dialog
    // For now, ItemDialog fetches its own select options based on formField config.
    setEditingItem(item);
    setDialogMode('edit');
    setIsItemDialogOpen(true);
  };

  const handleDeleteItem = async (item) => {
    if (!currentCategoryConfig || !window.confirm(`Are you sure you want to delete this ${currentCategoryConfig.nameField.replace('_name', '')}: "${item[currentCategoryConfig.nameField]}"?`)) {
        return;
    }
    setIsLoading(true);
    try {
        let path = currentCategoryConfig.apiPath;
        if (!path.endsWith('/')) {
            path += '/';
        }
        await categoryApi.deleteCategoryItem(`${path}${item[currentCategoryConfig.idField]}/`);
        showSnackbar(`${currentCategoryConfig.label} item deleted successfully!`, 'success');
        fetchCategoryItems(); 
    } catch (err) {
        console.error(`Error deleting ${currentCategoryConfig.label} item:`, err);
        const errorDetail = err.response?.data ? JSON.stringify(err.response.data) : err.message;
        showSnackbar(`Failed to delete ${currentCategoryConfig.label} item. Error: ${errorDetail}`, 'error');
    } finally {
        setIsLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>Category Management</Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="category-select-label">Select Category</InputLabel>
        <Select
          labelId="category-select-label"
          value={selectedCategoryPath}
          label="Select Category"
          onChange={handleCategoryChange}
        >
          <MenuItem value=""><em>-- Select a Category --</em></MenuItem>
          {MANAGED_CATEGORIES.map((cat) => (
            <MenuItem key={cat.apiPath} value={cat.apiPath}>{cat.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {currentCategoryConfig && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{currentCategoryConfig.label} List</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddItemDialog}
            disabled={isLoading || !currentCategoryConfig}
          >
            Add New Item
          </Button>
        </Box>
      )}

      {isLoading && !isItemDialogOpen && <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress /></Box>}
      {error && <Typography color="error" sx={{ my: 2 }}>{error}</Typography>}

      {!isLoading && currentCategoryConfig && categoryItems.length > 0 && (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table stickyHeader aria-label={`${currentCategoryConfig.label} table`}>
            <TableHead>
              <TableRow>
                {currentCategoryConfig.columns.map((column) => (
                  <TableCell key={column.id} style={{ minWidth: column.minWidth, fontWeight: 'bold' }}>
                    {column.label}
                  </TableCell>
                ))}
                <TableCell style={{ minWidth: 120, fontWeight: 'bold', textAlign: 'right' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categoryItems.map((item) => (
                <TableRow hover role="checkbox" tabIndex={-1} key={item[currentCategoryConfig.idField]}>
                  {currentCategoryConfig.columns.map((column) => (
                    <TableCell key={column.id}>
                      {renderCellContent(item, column)}
                    </TableCell>
                  ))}
                  <TableCell sx={{ textAlign: 'right' }}>
                    <Tooltip title="Edit">
                      <span>
                        <IconButton 
                          onClick={() => handleEditItem(item)} 
                          size="small" 
                          disabled={isLoading}
                        >
                          <EditIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                       <span>
                        <IconButton 
                          onClick={() => handleDeleteItem(item)} 
                          size="small" 
                          color="error" 
                          disabled={isLoading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {!isLoading && currentCategoryConfig && categoryItems.length === 0 && !error && (
        <Typography sx={{ mt: 2, textAlign: 'center' }}>No items found for {currentCategoryConfig.label}.</Typography>
      )}

      {currentCategoryConfig && isItemDialogOpen && (
        <ItemDialog
          open={isItemDialogOpen}
          onClose={handleCloseItemDialog}
          mode={dialogMode}
          categoryConfig={currentCategoryConfig}
          itemData={editingItem} 
          onSubmit={handleItemSubmit}
          loading={isLoading}
        />
      )}
    </Box>
  );
}

export default CategoryManagementPanel; 